/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { Select } from 'antd'
import type { CustomCellEditorProps } from 'ag-grid-react'
import { useEffect, useState } from 'react'

import colors from '@sportninja/common/constants/appColors'
import {
  dedupeAssignments,
  OFFICIAL_BUCKET_TYPES,
  OFFICIAL_BUCKETS,
  primaryTypeForBucket,
} from '@sportninja/common/features/game/gameListView/officialBuckets'
import type {
  OfficialAssignment,
  OfficialBucket,
} from '@sportninja/common/features/game/gameListView/officialBuckets'
import { capitalizeWords } from '@sportninja/common/ts-utils'

import { font, weightSemiBold } from 'src/components/css'

import type { OfficialOption } from '../hooks/useOfficialOptions'
import type { GameGridRow } from '../utils/gameGridRows'
import { gameViewsLabel } from '../utils/i18n'

export const OFFICIALS_FIELD_ID = 'officials-editor-people'
export const OFFICIALS_APPLY_TEST_ID = 'officials-editor-apply'
export const OFFICIALS_CANCEL_TEST_ID = 'officials-editor-cancel'

export interface OfficialTypeOption {
  id: number
  name: string
}

/** The other columns' headings, for explaining a disabled option. */
const BUCKET_LABEL_KEY: Record<OfficialBucket, string> = {
  referees: 'gameGridColReferees',
  scorekeepers: 'gameGridColScorekeepers',
  timekeepers: 'gameGridColTimekeepers',
}

const columnLabelFor = (bucket: OfficialBucket): string =>
  capitalizeWords(gameViewsLabel(BUCKET_LABEL_KEY[bucket]))

export interface OfficialsCellEditorParams {
  /** Which column this editor is serving, and therefore which roles it may set. */
  bucket: OfficialBucket
  /** Every assignable person, already unioned with the ones on loaded games. */
  officials: OfficialOption[]
  /**
   * uid → display name for EVERY known record, including the ones the duplicate
   * collapse dropped from `officials`. A game may reference the record that lost
   * the collapse, and its chip still has to read as a person.
   */
  officialNamesById: Map<string, string>
  /** `official_type.id` → label, for the per-person role dropdown. */
  officialTypes: OfficialTypeOption[]
  columnLabel: string
}

/**
 * Picks the people — and their roles — for ONE officials column.
 *
 * A game's officials are (person, role) PAIRS. Two referees are two people
 * holding the same role, and the same person can hold a role in more than one
 * column, so the multi-select is keyed on the person and the role is chosen
 * beside them. That is also why editing this column can never disturb another:
 * the commit carries only the roles this column owns.
 *
 * The role row appears only when the column owns more than one role — a
 * Timekeepers column with a single role would just be a dropdown with one
 * option — so seeding a new role on the backend widens the UI on its own.
 */
const OfficialsCellEditor = ({
  data,
  onValueChange,
  stopEditing,
  bucket,
  officials,
  officialNamesById,
  officialTypes,
  columnLabel,
}: CustomCellEditorProps<GameGridRow> & OfficialsCellEditorParams) => {
  const roleIds = OFFICIAL_BUCKET_TYPES[bucket]
  const primaryRole = primaryTypeForBucket(bucket)
  const canChooseRole = roleIds.length > 1

  const current: OfficialAssignment[] = data?.[bucket] ?? []

  /*
   * Held as ONE list rather than "selected ids" plus "roles by id": the pair is
   * the unit the API stores, and splitting it invites the two halves to drift
   * when a person is removed and re-added.
   */
  const [assignments, setAssignments] = useState<OfficialAssignment[]>(() =>
    dedupeAssignments(current)
  )

  /*
   * The pool, plus anyone this row ALREADY has.
   *
   * Duplicate officials are collapsed to one option per app account, and two
   * games in the same competition can legitimately reference opposite halves of
   * such a pair. Without this union the assigned person would be missing from
   * the list, and antd would render their chip as a raw uid.
   */
  const officialsById = new Map(
    officials.map((official) => [official.id, official])
  )
  current.forEach((assignment) => {
    if (!officialsById.has(assignment.officialId)) {
      officialsById.set(assignment.officialId, {
        id: assignment.officialId,
        /*
         * Named from the FULL map, not from `officials`.
         *
         * The duplicate collapse keeps one record per person, and a game can
         * reference the one it dropped — so this branch is the normal path for
         * those, not an edge case. Reading the name from the collapsed list
         * would find nothing and render the raw uid as a chip.
         */
        name:
          officialNamesById.get(assignment.officialId) ?? assignment.officialId,
        //  Nothing is known about this record beyond the assignment itself, so
        //  it carries no default role and no identity to be merged on.
        defaultTypeId: null,
        userId: null,
        identityHash: null,
      })
    }
  })
  /*
   * Anyone already working this game in ANOTHER column.
   *
   * One person cannot referee and keep score at the same time. Production has
   * 23,830 games where somebody holds both, but 20,523 of those landed in a
   * single 2022 import — it is overwhelmingly legacy data, not a workflow. They
   * are offered as DISABLED rather than hidden, with the column they are already
   * in, so the absence explains itself instead of looking like a missing person.
   *
   * Existing assignments are never taken away by this: the rule only governs
   * what can be added, so a game that already carries a dual role keeps it until
   * somebody edits that column.
   */
  const selectedHere = new Set(
    assignments.map((assignment) => assignment.officialId)
  )
  const assignedElsewhere = new Map<string, OfficialBucket>()
  OFFICIAL_BUCKETS.filter((other) => other !== bucket).forEach((other) => {
    const inOther: OfficialAssignment[] = data?.[other] ?? []
    inOther.forEach((assignment) => {
      //  Somebody picked HERE is never "elsewhere". Games predating this rule
      //  carry dual roles, and marking such a person disabled would grey out a
      //  live selection and tag their chip with another column's name.
      if (selectedHere.has(assignment.officialId)) {
        return
      }
      assignedElsewhere.set(assignment.officialId, other)
    })
  })

  const selectableOfficials = Array.from(officialsById.values())
  const roleLabels = new Map(officialTypes.map((type) => [type.id, type.name]))

  const nameFor = (officialId: string): string =>
    officialsById.get(officialId)?.name ?? officialId

  /**
   * A newly picked person starts on their own stored role when this column owns
   * it, and on the column's primary role otherwise — a scorekeeper dropped into
   * Referees becomes a Referee here, not a Scorekeeper smuggled into the wrong
   * column.
   */
  const initialRoleFor = (officialId: string): number => {
    const stored = officialsById.get(officialId)?.defaultTypeId
    return stored != null && roleIds.includes(stored) ? stored : primaryRole
  }

  const handleSelectionChange = (nextIds: string[]) => {
    setAssignments(
      nextIds.map((officialId) => {
        const existing = assignments.find(
          (assignment) => assignment.officialId === officialId
        )
        return (
          existing ?? {
            officialId,
            officialTypeId: initialRoleFor(officialId),
          }
        )
      })
    )
  }

  const handleRoleChange = (officialId: string, officialTypeId: number) => {
    setAssignments(
      assignments.map((assignment) =>
        assignment.officialId === officialId
          ? { ...assignment, officialTypeId }
          : assignment
      )
    )
  }

  /*
   * The panel node is STATE, not a ref, and the fields wait for it.
   *
   * Every dropdown portals into this panel so a pick lands inside the popup and
   * `stopEditingWhenCellsLoseFocus` does not read it as a click-outside. A ref
   * is still null on first paint, so the list would portal to `document.body` —
   * outside the grid — and the very first pick would close the editor. Anchoring
   * to the panel's own node rather than a queried `.ag-popup-editor` matters too:
   * AG Grid recreates that element when the editor re-renders.
   */
  const [panel, setPanel] = useState<HTMLDivElement | null>(null)
  const insideEditor = () => panel ?? document.body

  /*
   * Keep the whole panel — Apply included — inside the viewport.
   *
   * AG Grid drops a popup editor UNDER its cell and does not flip it when there
   * is no room. On a row near the bottom of a tall grid that put the panel 122px
   * past the fold, so the buttons were simply not on screen: the reported
   * "Apply is gone" had nothing to do with the dropdown covering it.
   *
   * Measured once, on mount, from the panel's own position: CSS cannot express
   * "as tall as the space left below me". The panel caps to that space and its
   * body scrolls; when even the minimum will not fit, it shifts up by the
   * overflow instead.
   */
  const [maxHeight, setMaxHeight] = useState<number>(PANEL_MAX_HEIGHT)
  const listHeight = Math.min(
    LIST_MAX_HEIGHT,
    Math.max(LIST_MIN_HEIGHT, maxHeight - PANEL_CHROME)
  )

  /*
   * How many option lists are showing, because the panel has to GROW to hold
   * whichever one is open.
   *
   * `max-height` alone was not enough: a panel with two officials in it is only
   * ~270px of content, so it shrank to that and the 200px list — anchored under
   * its field — ran out of the bottom and straight through the action bar. A
   * floor applied only while a list is open keeps the closed panel compact and
   * still gives the open one somewhere to go.
   *
   * COUNTED, and counting EVERY list including the per-person role pickers. A
   * boolean fed only by the people field left the role list overflowing the
   * panel — it rendered below Cancel and Apply, detached from its own field —
   * and a shared boolean would be cleared by whichever list closed first.
   */
  const [openListCount, setOpenListCount] = useState(0)
  const trackListVisibility = (open: boolean) =>
    setOpenListCount((count) => Math.max(0, count + (open ? 1 : -1)))

  useEffect(() => {
    if (panel === null) {
      return
    }
    const available =
      window.innerHeight - panel.getBoundingClientRect().top - VIEWPORT_MARGIN
    setMaxHeight(
      Math.min(PANEL_MAX_HEIGHT, Math.max(PANEL_MIN_HEIGHT, available))
    )

    /*
     * When even the minimum will not fit below the cell, move AG GRID'S POPUP,
     * not the panel inside it.
     *
     * A `transform` on the panel was the obvious move and it was wrong: it
     * shifts what is PAINTED and leaves the box where it was. AG Grid gives that
     * box a `0 0 20px rgba(0,0,0,.67)` shadow, so the panel slid up and left its
     * own shadow behind — a dimmed rectangle hanging below the buttons over the
     * grid, which is exactly what it looked like.
     *
     * Adjusting the container's own inline `top` moves box, shadow and contents
     * together. Reaching into AG Grid's element is deliberate: it owns the
     * positioning, it positions popups UNDER the cell without ever flipping
     * them, and nothing it exposes can express "keep this on screen".
     */
  }, [panel])

  const commit = () => {
    //  The array itself is the cell value. `VenueCellEditor` encodes its two
    //  halves into a string only because it commits two COLUMNS through one;
    //  this is one column's value, and an encoded token in an array-typed field
    //  would break the column's formatter until the patch landed.
    onValueChange(dedupeAssignments(assignments))
    stopEditing()
  }

  return (
    /*
     * No blanket `preventDefault` on mousedown: antd opens a Select ON
     * mousedown, so suppressing it leaves every dropdown here permanently shut.
     * Focus containment is handled by `getPopupContainer` instead.
     */
    <div
      css={panelStyles}
      ref={setPanel}
      style={{
        maxHeight,
        //  While a list is open the panel fills its allowance, so the list has
        //  somewhere to go and the action bar sits at the bottom rather than
        //  halfway up.
        minHeight: openListCount > 0 ? maxHeight : undefined,
      }}
    >
      <div css={bodyStyles}>
        {panel !== null && (
          <>
            <label css={fieldStyles}>
              <span css={labelStyles}>{columnLabel}</span>
              <Select
                //  An explicit id: without one, sibling Selects in this panel all
                //  advertise the same `aria-controls` and nothing — assistive tech
                //  included — can tell which list belongs to which field.
                id={OFFICIALS_FIELD_ID}
                mode='multiple'
                autoFocus
                value={assignments.map((assignment) => assignment.officialId)}
                onChange={handleSelectionChange}
                options={selectableOfficials.map((official) => {
                  const elsewhere = assignedElsewhere.get(official.id)
                  const name =
                    official.name === '' ? official.id : official.name
                  return {
                    value: official.id,
                    label:
                      elsewhere === undefined
                        ? name
                        : `${name} — ${columnLabelFor(elsewhere)}`,
                    disabled: elsewhere !== undefined,
                  }
                })}
                placeholder={gameViewsLabel('gameGridOfficialsPlaceholder')}
                notFoundContent={gameViewsLabel('gameGridOfficialsEmptyPool')}
                showSearch
                optionFilterProp='label'
                //  Cut to the room left inside the panel, so the list always
                //  ends above the action bar. It scrolls internally past this.
                listHeight={listHeight}
                onDropdownVisibleChange={trackListVisibility}
                style={selectStyle}
                getPopupContainer={insideEditor}
              />
            </label>

            {canChooseRole &&
              assignments.map((assignment) => (
                <label
                  key={assignment.officialId}
                  css={roleRowStyles}
                  htmlFor={`${OFFICIALS_FIELD_ID}-role-${assignment.officialId}`}
                >
                  <span css={roleNameStyles}>
                    {nameFor(assignment.officialId)}
                  </span>
                  <Select
                    id={`${OFFICIALS_FIELD_ID}-role-${assignment.officialId}`}
                    aria-label={`${gameViewsLabel(
                      'gameGridOfficialsRole'
                    )} — ${nameFor(assignment.officialId)}`}
                    value={assignment.officialTypeId}
                    onChange={(next: number) =>
                      handleRoleChange(assignment.officialId, next)
                    }
                    options={roleIds.map((roleId) => ({
                      value: roleId,
                      label: roleLabels.get(roleId) ?? String(roleId),
                    }))}
                    style={roleSelectStyle}
                    onDropdownVisibleChange={trackListVisibility}
                    getPopupContainer={insideEditor}
                  />
                </label>
              ))}
          </>
        )}
      </div>

      <div css={actionsStyles}>
        <button
          type='button'
          css={secondaryButtonStyles}
          onClick={() => stopEditing(true)}
          data-testid={OFFICIALS_CANCEL_TEST_ID}
        >
          {capitalizeWords(gameViewsLabel('bulkCancel'))}
        </button>
        <button
          type='button'
          css={primaryButtonStyles}
          onClick={commit}
          data-testid={OFFICIALS_APPLY_TEST_ID}
        >
          {capitalizeWords(gameViewsLabel('bulkApply'))}
        </button>
      </div>
    </div>
  )
}

// ─── Styles ──────────────────────────────────────────────────────

//  antd sizes its Select through `style`, not a class, so these stay plain
//  objects — hoisted out of the JSX so every field shares the one width.
const selectStyle = { width: 300 }
const roleSelectStyle = { width: 150 }

/*
 * Both halves of the stacking comparison are pinned here rather than assuming
 * antd's default: the action bar has to out-stack the option list, and reading
 * one of the two numbers off the theme makes that a guess.
 */
/*
 * The action bar has to out-stack the option list, and the list is NOT at antd's
 * default: `web/src/index.scss` raises every `.ant-select-dropdown` in the app to
 * 999999. Sitting just above that is the only value that actually wins, and
 * lowering the list instead would mean fighting a global rule from a component.
 */
const DROPDOWN_Z_INDEX = 999999
const ACTIONS_Z_INDEX = DROPDOWN_Z_INDEX + 1

/*
 * Panel sizing.
 *
 * The panel takes the room that exists BELOW ITS CELL and the option list is cut
 * to fit inside it. The alternative — a fixed-height panel moved up when it does
 * not fit — was tried and abandoned twice: a `transform` shifts the paint but
 * not the box, so AG Grid's popup shadow stayed behind as a dimmed rectangle
 * hanging under the buttons; and writing the popup's own `top` loses a race,
 * because AG Grid positions it AFTER mounting the editor and simply overwrites
 * the value, deferred or not.
 *
 * Fitting instead of moving needs no cooperation from AG Grid at all.
 *
 * `PANEL_CHROME` is everything in the panel that is not the list — the label,
 * the field, the gaps, the action bar and the padding — so the list gets what is
 * left. It is bounded below so the list never shrinks to a slit; a panel with
 * less room than that scrolls its body instead.
 */
const PANEL_MAX_HEIGHT = 520
const PANEL_MIN_HEIGHT = 220
const PANEL_CHROME = 170
const LIST_MAX_HEIGHT = 192
const LIST_MIN_HEIGHT = 96
const VIEWPORT_MARGIN = 16

const panelStyles = css`
  ${font.body}
  display: flex;
  flex-direction: column;
  padding: 12px;
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.45);
  //  A crew can run to half a dozen people; the panel caps its height rather
  //  than growing past the viewport.
  //  The real cap is measured at mount and applied inline; this is the floor a
  //  panel falls back to before that runs.
  //  The dropdowns portal in here, so this is their positioning context. NOT
  //  scrollable itself — the body below is — or an open list would be clipped.
  position: relative;
`

/*
 * The fields, scrolling independently of the action bar.
 *
 * `min-height: 0` is what actually lets it scroll: a flex child defaults to
 * min-content, so without it the body refuses to shrink and the panel grows past
 * its own max-height, taking the buttons off screen.
 */
const bodyStyles = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  /*
   * GROWS to fill the panel, which is what pins the action bar to the true
   * bottom. Without it the body is only as tall as its content, the bar rides up
   * directly beneath it, and the option list — which is as tall as the panel
   * allows — carries on past the bar and appears to be cut in half by it.
   *
   * min-height 0 is what lets it scroll at all: a flex child defaults to
   * min-content, so without it the body refuses to shrink and the panel grows
   * past its own max-height, taking the buttons off screen.
   */
  flex: 1 1 auto;
  min-height: 0;
`

const fieldStyles = css`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const labelStyles = css`
  ${weightSemiBold}
  font-size: 11px;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_50};
`

const roleRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const roleNameStyles = css`
  font-size: 13px;
  color: ${colors.NEUTRAL_0};
  //  Long names truncate rather than wrapping the row and misaligning the
  //  dropdowns beside them.
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/*
 * Cancel and Apply, which must NEVER be covered.
 *
 * Two things make that true, and both are required:
 *
 * 1. It is a sibling of the scrolling body, not part of it — so a long crew
 *    scrolls under it instead of pushing it out of the panel.
 * 2. It out-stacks the dropdowns. Every list portals into this panel and antd
 *    positions them absolutely at z-index 1050; an open list painted straight
 *    over Apply, and the only way to commit was to close the list first. A
 *    positioned element at a higher z-index, with an OPAQUE background (or the
 *    list shows through it), keeps both buttons visible and clickable whatever
 *    is open.
 *
 * The list is capped by listHeight and scrolls internally, so the few options
 * this bar covers are still reachable.
 */
const actionsStyles = css`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  flex: 0 0 auto;
  position: relative;
  z-index: ${ACTIONS_Z_INDEX};
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${colors.SECONDARY_200};
  background: ${colors.SECONDARY_400};
`

const secondaryButtonStyles = css`
  ${font.body}
  height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  border: 1px solid ${colors.SECONDARY_100};
  background: transparent;
  color: ${colors.NEUTRAL_0};
  cursor: pointer;
`

const primaryButtonStyles = css`
  ${font.body}
  ${weightSemiBold}
  height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  border: none;
  background: ${colors.PRIMARY};
  color: ${colors.PRIMARY_TEXT};
  cursor: pointer;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

export default OfficialsCellEditor
